<template>
  <tm-button
    class="tm-icon-button"
    :text-link="textLink"
    :size="size"
    :color="color"
    :icon-only="iconOnly"
    :disable="disabled"
    :to="to"
    :loading="loading"
    :loading-title="loadingTitle || $t('common.loading')"
    :x-padding="xPadding"
  >
    <tm-icon
      v-if="hasIcon"
      :class="iconClass"
      :name="iconName!"
      :size="cIconSize"
      :color="iconColor"
      :left="hasContent"
      :disabled="iconDisabled"
    />
    <slot v-if="!iconOnly">{{ title }}</slot>
  </tm-button>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { computed, useSlots } from '@/composition/vue/compositionApi'
import TmButton from '@/components/shared/TmButton.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import type { ButtonSize, IconSize, ButtonColor, TmButtonXPadding } from '@/components/shared/types'
import { useBreakpoints } from '@/composition/breakpoints'
import type { IconName } from '@/assets/icons/icons'
import type { VueLikeClassAttr } from '@/components/types'
import type { StyleColors } from '@/constants/styleColors'

const props = withDefaults(
  defineProps<{
    title?: string
    textLink?: boolean
    size?: ButtonSize
    iconSize?: IconSize
    iconName?: IconName
    iconColor?: StyleColors
    iconClass?: VueLikeClassAttr
    iconOnly?: boolean
    iconDisabled?: boolean
    disabled?: boolean
    hideIconMobile?: boolean
    to?: RouteLocationRaw
    loading?: boolean
    loadingTitle?: string
    color?: ButtonColor
    xPadding?: TmButtonXPadding
  }>(),
  {
    size: 'large',
  },
)

const { isMobile } = useBreakpoints()

const sizesMap: Record<ButtonSize, IconSize> = {
  xxSmall: 'xxSmall',
  xSmall: 'xxSmall',
  small: 'xxSmall',
  medium: 'xSmall',
  xMedium: 'small',
  large: 'small',
  '': 'medium',
  xLarge: 'large',
  xxLarge: 'xLarge',
}

const slots = useSlots()
const hasContent = computed(() => (!!props.title || !!slots.default) && !props.iconOnly)
const hasIcon = computed(() => props.iconName && !(isMobile.value && props.hideIconMobile))
const cIconSize = computed(() => props.iconSize || sizesMap[props.size])
</script>

<style lang="scss" scoped>
.tm-icon-button {
  justify-content: center;
}

:deep(.tm-btn:not([disabled]):not(.q-btn--outline):not(.q-btn--flat)) {
  &:hover:before {
    background-color: $input !important;
  }
}
</style>
